/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { catchError, map } from 'rxjs/operators';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { TenantDataService } from './data.service';
import { RestClientService } from 'app/shared/services/rest-client.service';
import {
  CreateUserRequest,
  DeleteUserRequest,
  TenantDetails,
  TenantDetailsUpdateRequest,
  TenantDetailsWithFlags,
  User
} from 'app/page-modules/tenant-management/models';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { observableThrow } from 'app/shared/rxjs/rxjs.utils';

/** Provides data from the management api. */
@Injectable()
export class ApiTenantDataService extends TenantDataService {
  /**
   * Constructs an ApiDataService instance
   * @param client the rest client instance to use
   */
  constructor(private client: RestClientService) {
    super();
  }

  createTenant(tenant: TenantDetails): Observable<TenantDetails> {
    return this.client
      .post<TenantDetails>(Location.joinWithSlash(environment.baseUrl, 'tenants'), tenant)
      .pipe(
        map(res => res.body!),
        catchError(() => observableThrow('Error creating tenant'))
      );
  }

  deleteTenant(organizationId: string): Observable<void> {
    return this.client
      .delete(Location.joinWithSlash(environment.baseUrl, `tenants/${organizationId}`))
      .pipe(
        map(() => undefined),
        catchError(() => observableThrow('Error deleting tenant'))
      );
  }

  tenantDetails(organizationId: string): Observable<TenantDetailsWithFlags> {
    return this.client
      .get<TenantDetailsWithFlags>(
        Location.joinWithSlash(environment.baseUrl, `tenants/${organizationId}`)
      )
      .pipe(
        map(res => res.body!),
        catchError(() => observableThrow('Error loading tenant details'))
      );
  }

  updateTenantDetails(
    organizationId: string,
    successMessage: string,
    updateRequest: TenantDetailsUpdateRequest
  ): Observable<TenantDetailsUpdateRequest> {
    // This is required because of custom-authorizer checks
    const formattedRequest = {
      ...updateRequest,
      tenantDetails: {
        ...updateRequest.tenantDetails,
        history: updateRequest.tenantDetails.history
      }
    };

    return this.client
      .patch<TenantDetails>(
        Location.joinWithSlash(environment.baseUrl, `tenants/${organizationId}`),
        formattedRequest
      )
      .pipe(
        map(res => ({ successMessage, tenantDetails: res.body! })),
        catchError(() => observableThrow('Error updating tenant details'))
      );
  }

  addReadOnlyRole(organizationId: string): Observable<void> {
    return this.client
      .patch(
        Location.joinWithSlash(environment.baseUrl, `tenants/${organizationId}/read-only-role`),
        ''
      )
      .pipe(
        map(() => undefined),
        catchError(() => observableThrow('Error adding read-only role for tenant'))
      );
  }

  usersForTenant(organizationId: string): Observable<User[]> {
    return this.client
      .get<User[]>(Location.joinWithSlash(environment.baseUrl, `tenants/${organizationId}/users`))
      .pipe(
        map(res => res.body!),
        catchError(() => observableThrow('Error loading users for tenant'))
      );
  }

  addUserForTenant(organizationId: string, createUserRequest: CreateUserRequest): Observable<User> {
    return this.client
      .post<User>(
        Location.joinWithSlash(environment.baseUrl, `tenants/${organizationId}/users`),
        createUserRequest
      )
      .pipe(
        map(res => res.body!),
        catchError(() => observableThrow('Error creating user for tenant'))
      );
  }

  updateUserForTenant(organizationId: string, user: User): Observable<User> {
    return this.client
      .patch<User>(Location.joinWithSlash(environment.baseUrl, `users/${user.email}`), user)
      .pipe(
        map(res => res.body!),
        catchError(() => observableThrow('Error updating user for tenant'))
      );
  }

  deleteUser(user: User, deleteUserRequest: DeleteUserRequest): Observable<User> {
    return this.client
      .delete(
        Location.joinWithSlash(
          environment.baseUrl,
          `tenants/${user.organizationId}/users/${user.email}`
        ),
        deleteUserRequest
      )
      .pipe(
        map(() => user),
        catchError(() => observableThrow('Error deleting user for tenant'))
      );
  }
}
